import { request, ApiError } from './client'

/**
 * Writes that survive losing the network.
 *
 * <p>Benin field conditions are the whole reason this exists: a client filling
 * in a request in a courtyard with one bar, a provider marking a delivery in a
 * stairwell. Losing that work because the radio dropped for four seconds is the
 * failure the spec's §Contraintes techniques is about.
 *
 * <p>IndexedDB rather than `localStorage`: the outbox must survive a tab being
 * killed mid-write, and `localStorage` is synchronous and capped at a few
 * megabytes shared with everything else.
 *
 * <p><b>Only idempotent writes belong here.</b> Every queued request carries a
 * key the server dedupes on, because "retry until it works" and "exactly once"
 * are the same requirement seen from two sides — without the key a flaky
 * connection becomes two missions or two payments.
 */
const DB_NAME = 'waago-outbox'
const STORE = 'requests'
const DB_VERSION = 1

export type OutboxEntry = {
  id: string
  path: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body: unknown
  idempotencyKey: string
  /** What to tell the person if it never succeeds. */
  label: string
  queuedAt: number
  attempts: number
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('outbox unavailable'))
  })
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  const db = await open()
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const req = fn(tx.objectStore(STORE))
    req.onsuccess = () => resolve(req.result as T)
    req.onerror = () => reject(req.error ?? new Error('outbox write failed'))
    tx.oncomplete = () => db.close()
  })
}

export async function queued(): Promise<OutboxEntry[]> {
  try {
    const all = await withStore<OutboxEntry[]>('readonly', (s) => s.getAll())
    return all.sort((a, b) => a.queuedAt - b.queuedAt)
  } catch {
    // A browser with IndexedDB disabled or in a private window that refuses it.
    // The app still works; it simply cannot queue.
    return []
  }
}

async function put(entry: OutboxEntry): Promise<void> {
  await withStore('readwrite', (s) => s.put(entry))
}

async function remove(id: string): Promise<void> {
  await withStore('readwrite', (s) => s.delete(id))
}

/** Listeners for "how many are waiting", so a screen can show a badge. */
const watchers = new Set<(count: number) => void>()

async function announce() {
  const count = (await queued()).length
  watchers.forEach((w) => {
    w(count)
  })
}

export function watchOutbox(listener: (count: number) => void): () => void {
  watchers.add(listener)
  void announce()
  return () => watchers.delete(listener)
}

/**
 * Sends now, or queues for when the network returns.
 *
 * <p>Tries first rather than checking `navigator.onLine`, because that flag
 * only means "attached to a network" — a captive portal, a Wi-Fi with no route
 * out, and a dead 3G cell all report online. The request itself is the only
 * honest test.
 *
 * @returns whether it went out immediately
 */
export async function sendOrQueue(
  entry: Omit<OutboxEntry, 'queuedAt' | 'attempts'>,
): Promise<boolean> {
  try {
    await request(entry.path, {
      method: entry.method,
      body: entry.body,
      idempotencyKey: entry.idempotencyKey,
    })
    return true
  } catch (e) {
    // A refusal is not a network problem, and queueing it would retry a
    // request the server has already judged — forever. Only a failure to
    // *reach* the server is worth keeping.
    if (e instanceof ApiError) throw e

    await put({ ...entry, queuedAt: Date.now(), attempts: 0 })
    void announce()
    return false
  }
}

/**
 * Replays everything waiting, oldest first.
 *
 * <p>Order matters: two updates to the same mission must arrive in the order
 * they were made, so this is sequential rather than parallel. A single entry
 * that the server *refuses* is dropped rather than retried forever — it has
 * been judged, and a queue that never empties is a queue nobody trusts.
 */
export async function flushOutbox(): Promise<{ sent: number; failed: number }> {
  const entries = await queued()
  let sent = 0
  let failed = 0

  for (const entry of entries) {
    try {
      await request(entry.path, {
        method: entry.method,
        body: entry.body,
        // The same key it was queued with, which is what makes a replay safe:
        // if the original did land before the connection dropped, the server
        // returns that result instead of doing it twice.
        idempotencyKey: entry.idempotencyKey,
      })
      await remove(entry.id)
      sent++
    } catch (e) {
      if (e instanceof ApiError) {
        // Judged and refused. Keeping it would mean retrying it on every
        // reconnect for the life of the install.
        await remove(entry.id)
        failed++
      } else {
        // Still no network. Leave it and stop: the rest are newer and there is
        // no point hammering a connection that is not there.
        await put({ ...entry, attempts: entry.attempts + 1 })
        break
      }
    }
  }

  void announce()
  return { sent, failed }
}

/**
 * Flushes whenever the browser thinks it is back, and once on start.
 *
 * <p>`online` is unreliable in the direction that matters — it fires when a
 * network is attached, not when one works — but a false positive here costs a
 * failed request and a retry, which is exactly what the outbox is for.
 */
export function startOutbox(): () => void {
  const flush = () => {
    void flushOutbox()
  }
  window.addEventListener('online', flush)
  flush()
  return () => window.removeEventListener('online', flush)
}
